import * as AvatarPrimitive from '@radix-ui/react-avatar';
import cn from 'classnames';
import React, { CSSProperties } from 'react';

import { DEFAULT_AVATAR_SIZE } from '@/shared/config';

import { avatarColorHash } from './Avatar.config';
import styles from './Avatar.module.scss';
import { AvatarGroup } from './AvatarGroup';

type AvatarProps = {
  size?: number;
  src?: string;
  alt?: string;
  initials?: string;
  disabled?: boolean;
  count?: number;
  // Soft tint instead of the hashed colour, for recipients without a profile
  tinted?: boolean;
  className?: string;
  testID?: string;
};

type CompoundedComponent = React.ForwardRefExoticComponent<
  AvatarProps & React.RefAttributes<HTMLSpanElement>
> & {
  Group: typeof AvatarGroup;
};

const InternalAvatar: React.ForwardRefRenderFunction<
  HTMLSpanElement,
  AvatarProps
> = (props, ref) => {
  const {
    size = DEFAULT_AVATAR_SIZE,
    src,
    alt,
    initials,
    disabled,
    count,
    tinted,
    className,
    testID,
    ...restAvatarProps
  } = props;

  const isCount = Boolean(count);
  const style: CSSProperties = { width: size, height: size };

  if (!isCount && !tinted && initials) {
    style.backgroundColor = avatarColorHash.hex(initials);
  }

  const parsedInitials = initials?.replace(/[\s.]/g, ''); // remove spaces and dots

  return (
    <AvatarPrimitive.Root
      {...restAvatarProps}
      ref={ref}
      className={cn(
        styles.avatar,
        disabled && styles.disabled,
        isCount && styles.isCount,
        tinted && styles.tinted,
        className,
      )}
      data-testid={testID}
      style={style}
    >
      {isCount ? null : (
        <AvatarPrimitive.Image
          className={styles.avatarImage}
          src={src}
          alt={alt}
        />
      )}
      <AvatarPrimitive.Fallback
        className={cn(styles.avatarFallback, 'h6')}
        delayMs={100}
      >
        {count ? `${count}+` : parsedInitials}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
};

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  InternalAvatar,
) as CompoundedComponent;

Avatar.Group = AvatarGroup;

export { Avatar };
